<template>
  <Listbox
    :id="attrs.id"
    v-model="selectedItem"
    as="div"
    :class="['headlessui-listbox', attrs.class]"
  >
    <ListboxButton :aria-label="placeholder" v-bind="buttonAttrs">{{ displayText }}</ListboxButton>
    <ListboxOptions :unmount="false">
      <ListboxOption v-for="item in dataItems" :key="`${getItemValue(item)}`" :value="item">
        {{ getItemText(item) }}
      </ListboxOption>
    </ListboxOptions>
  </Listbox>
</template>

<script setup lang="ts">
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/vue';
import { computed, useAttrs } from 'vue';

/// Type
type Item = string | number | object | null;

/// Setup
defineOptions({ inheritAttrs: false });
const emit = defineEmits(['change']);
const model = defineModel<Item>({ default: null });
const props = defineProps({
  dataItems: { type: Array<Item>, default: () => [] },
  textField: { type: String, default: '' },
  valueField: { type: String, default: '' },
  placeholder: { type: String, default: 'Select an option' },
});

/// Data
const rootAttrs = new Set(['id', 'class']);

/// Composed
const attrs = useAttrs();

/// Computed
const buttonAttrs = computed(() =>
  Object.fromEntries(Object.entries(attrs).filter(([key]) => !rootAttrs.has(key))),
);
const itemLookup = computed(() => {
  if (!props.valueField) return null;
  return new Map(props.dataItems.map((x) => [getItemValue(x), x]));
});
const selectedItem = computed({
  get() {
    if (model.value === null || model.value === undefined) return null;
    if (itemLookup.value) return itemLookup.value.get(model.value);
    return model.value;
  },
  set(item) {
    model.value = getItemValue(item);
    emit('change', model.value);
  },
});
const displayText = computed(() => {
  if (selectedItem.value === null || selectedItem.value === undefined) return props.placeholder;
  return getItemText(selectedItem.value);
});

/// Methods
function getItemText(item: Item): string {
  if (item === null || item === undefined) return '';
  if ((props.textField || props.valueField) && typeof item === 'object') {
    return `${item[(props.textField || props.valueField) as keyof Item]}`;
  }
  return `${item}`;
}
function getItemValue(item?: Item): Item {
  if (item === null || item === undefined) return null;
  if (props.valueField && typeof item === 'object') {
    return item[props.valueField as keyof Item] as Item;
  }
  return item;
}
</script>
